import { FunctionComponent, useEffect, useState } from 'react';
import { Label } from '@shopify/polaris';
import styled from 'styled-components';
import ToggleButton from 'react-toggle';
import 'react-toggle/style.css';
import { StyledElement } from '@ui/types';

export type ToggleProps = {
  activeText?: React.ReactNode;
  isActive: boolean;
  loading?: boolean;
  disabled?: boolean;
  inActiveText?: React.ReactNode;
  id: string;
  onToggle?: (status: boolean) => void;
  preventToggleWhenDisabled?: boolean;
} & StyledElement;

const Container = styled.div`
  display: flex;
  align-items: center;
  margin: 0 4px 0;

  .Polaris-Label {
    label {
      font-weight: 500;
      margin-bottom: 0;
    }
    margin-right: 1.25rem;
  }

  .react-toggle-track {
    border: 2px solid ${({ theme }) => theme.colors.font.primary.hex};
  }

  .react-toggle-track,
  .react-toggle:hover .react-toggle-track,
  .react-toggle-track:hover {
    background: transparent;
  }

  .react-toggle--checked .react-toggle-track,
  .react-toggle--checked .react-toggle-track:hover {
    background-color: ${({ theme }) => theme.colors.primary.hex} !important;
  }

  .react-toggle-track:hover {
    background-image: linear-gradient(rgba(0, 0, 0, 0.2) 0 0) !important;
  }

  .react-toggle-thumb {
    background-color: ${({ theme }) => theme.colors.font.primary.hex};
    border: none;
    height: 14px;
    width: 14px;
    top: 4.5px;
    left: 6px;
  }

  .react-toggle--focus .react-toggle-thumb {
    box-shadow: none;
  }

  .react-toggle--checked .react-toggle-thumb {
    left: 30px;
  }
`;

const SlidingToggle: FunctionComponent<ToggleProps> = ({
  activeText,
  className,
  disabled,
  id,
  inActiveText,
  isActive,
  loading,
  onToggle,
  preventToggleWhenDisabled,
}) => {
  const [active, setActive] = useState(isActive);

  const getLabelText = () => {
    if (disabled) {
      return 'Unavailable';
    } else if (loading) {
      return inActiveText;
    }

    return active ? activeText : inActiveText;
  };

  useEffect(() => {
    setActive(isActive);
  }, [isActive, loading]);

  const onToggleButton = () => {
    const nextState = !active;

    if (!preventToggleWhenDisabled) {
      setActive(nextState);
    }

    if (onToggle) {
      onToggle(nextState);
    }
  };

  return (
    <Container className={className}>
      <Label id={id}>{getLabelText()}</Label>
      <ToggleButton
        checked={active}
        disabled={
          (disabled && (!active || preventToggleWhenDisabled)) || loading
        }
        icons={false}
        onChange={onToggleButton}
      />
    </Container>
  );
};

export default SlidingToggle;
